<template>
    <div class="segment-chooser">
        <pendo-multiselect
            v-bind="$attrs"
            ref="segmentChooser"
            :class="`segment-chooser--${theme}`"
            :disabled="disabled"
            :full-width="fullWidth"
            value-key="id"
            label-key="name"
            :no-results-text="$t('segmentChooser.multiselect.No segments found')"
            :placeholder="$t('segmentChooser.multiselect.Select Segment')"
            :value="model"
            :loading="loading || waitingOnValue"
            :max-menu-height="400"
            :min-menu-width="responsiveMinMenuWidth"
            :max-menu-width="responsiveMaxMenuWidth"
            :min-trigger-width="minTriggerWidth"
            :max-trigger-width="maxTriggerWidth"
            :trigger-size="triggerSize"
            :options="groupedSegmentOptions"
            :allow-empty="allowEmpty"
            group-options-key="segments"
            group-label-key="name"
            :popup-type="segmentChooserPopupType"
            :popper-options="{ class: `segment-chooser__panel--${theme}` }"
            @open="onOpen"
            @before-close="onBeforeClose"
            @highlighted-option-change="onHighlightedOptionChange"
            @filtered-options-change="onFilteredOptionsChange"
            @input-value-change="updateSearchValue"
            @select="updateValue"
            @leave-lower-bound="onLeaveLowerBound">
            <template
                #topLabel
                v-if="$slots.topLabel">
                <slot name="topLabel" />
            </template>
            <template
                #trigger
                v-if="useDataMeasureTrigger">
                <pendo-data-measure-trigger />
            </template>
            <template #selectedLabel="{ option }">
                <span
                    class="segment-chooser-option__label"
                    data-cy="segment-chooser-option-label">
                    {{ optionName(option) }}
                </span>
                <span class="segment-chooser-option__aside">
                    {{ optionAside(option) }}
                </span>
            </template>
            <template #option="{ option, index }">
                <pendo-option-insert
                    v-if="option.default"
                    v-pendo-tooltip="{
                        content: option.disabled ? disabledTooltip : '',
                        multiline: true,
                        arrow: true,
                        placement: 'auto',
                    }"
                    :option="option"
                    :index="index" />
                <div
                    v-else-if="option.id === 'empty-favorites-placeholder'"
                    class="segment-chooser-option__favorites-placeholder">
                    {{ $t('segmentChooser.multiselect.No favorites yet') }}
                </div>
                <pendo-option-insert
                    v-else-if="isVerified(option)"
                    v-pendo-tooltip="option.tooltip && {
                        content: option.tooltip.content,
                        multiline: true,
                        arrow: true,
                        placement: 'top',
                    }"
                    :option="option"
                    :actions="getActions(option)"
                    :index="index"
                    @edit="edit"
                    @copy="copy">
                    {{ optionName(option) }}
                    <pendo-icon
                        aria-hidden="true"
                        type="shield-check"
                        fill="var(--palette-green-10)"
                        stroke="var(--palette-green-80)"
                        size="14" />
                </pendo-option-insert>
                <pendo-option-insert
                    v-else
                    v-pendo-tooltip="option.tooltip && {
                        content: option.tooltip.content,
                        multiline: true,
                        arrow: true,
                        placement: 'top',
                    }"
                    :option="option"
                    :actions="getActions(option)"
                    :index="index"
                    @edit="edit"
                    @copy="copy" />
            </template>
            <template #aside>
                <segment-hover-card
                    v-if="shouldShowAsidePanel"
                    :segment-id="highlightedOptionId"
                    :segment-dependents="highlightedSegmentDependents"
                    height="400px"
                    @edit="edit"
                    @copy="copy" />
            </template>
            <template #footer="{ handleKeydown }">
                <div
                    v-if="allowCreateNewSegment"
                    v-pendo-tooltip="
                        isFreeAndOverMauLimit && {
                            content: overMauLimitMessage,
                            arrow: true,
                        }
                    "
                    class="segment-chooser__footer">
                    <pendo-button
                        ref="createNewSegmentButton"
                        class="segment-chooser__create-new-button"
                        type="tertiary"
                        theme="app"
                        :disabled="isFreeAndOverMauLimit"
                        prefix-icon="plus"
                        :label="$t('segmentChooser.multiselect.Create new segment')"
                        @keydown="handleKeydown($event, footerKeydownOverrides())"
                        @click="createNewSegment" />
                </div>
            </template>
        </pendo-multiselect>
        <edit-segment-overlay
            :visible="isEditingSegment"
            :guides-only="guidesOnly"
            :is-replay="isReplay"
            :target-app-id="targetAppId"
            :z-index="createOverlayZIndex"
            @delete="deleteSegment"
            @save="saveSegment"
            @close="isEditingSegment = false" />
    </div>
</template>

<script>
import { useWindowSize } from '@vueuse/core';
import { PendoButton, PendoIcon, PendoDataMeasureTrigger, PendoMultiselect, PendoOptionInsert, PendoTooltip, PendoMultiselectHelpers } from '@pendo/components';
import SegmentHoverCard from '@/common/hover-card/segment-hover-card/segment-hover-card.vue';
import { overMauLimitMessage } from '@/free/free.constants';
import EditSegmentOverlay from '@/segments/components/segment-editor-vue/edit-segment-overlay.vue';
import { onLeaveLowerBound } from '@/utils/multiselect-keyboard';
import { segmentFiltersHaveAppIdMismatch } from '@/utils/segments.js';
import { getResponsiveMenuWidth, shouldShowAsidePanel as shouldShowAsidePanelUtil } from '@/utils/responsive-multiselect.utils';
import { $canSub } from '@pendo/permissions';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { mapActions, mapGetters, mapState } from 'vuex';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
const { actionButtonKeyOverride, verticalNavigationKeyOverride } = PendoMultiselectHelpers;

export default {
    name: 'SegmentChooser',
    components: {
        PendoButton,
        PendoIcon,
        EditSegmentOverlay,
        PendoMultiselect,
        PendoOptionInsert,
        PendoDataMeasureTrigger,
        SegmentHoverCard,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        value: {
            type: [Object, String],
            default: null,
        },
        guidesOnly: {
            type: Boolean,
            default: false,
        },
        excludeAnonDefault: {
            type: Boolean,
            default: false,
        },
        allowCustomSegment: {
            type: Boolean,
            default: false,
        },
        allowCreateNewSegment: {
            type: Boolean,
            default: false,
        },
        disabledDefaults: {
            type: Array,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disableFirstVisitFilters: {
            type: Boolean,
            default: false,
        },
        waitOnValue: {
            type: Boolean,
            default: false,
        },
        theme: {
            type: String,
            default: 'dark',
        },
        shared: {
            type: Boolean,
            default: false,
        },
        targetAppId: {
            type: Number,
            default: null,
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
        maxTriggerWidth: {
            type: Number,
            default: 368,
        },
        minTriggerWidth: {
            type: Number,
            default: undefined,
        },
        minMenuWidth: {
            type: Number,
            default: 720,
            validator: (value) => value >= 300,
        },
        maxMenuWidth: {
            type: Number,
            default: 720,
            validator: (value) => value >= 300,
        },
        createOverlayZIndex: {
            type: Number,
            default: 20000, // this z-index is get the drawers overtop of the resource center
        },
        allowEmpty: {
            type: Boolean,
            default: false,
        },
        useDataMeasureTrigger: {
            type: Boolean,
            default: false,
        },
        triggerSize: {
            type: String,
            default: undefined,
        },
        isReplay: {
            type: Boolean,
            default: false,
        },
        allowNoOneSegment: {
            type: Boolean,
            default: false,
        },
        disabledTooltip: {
            type: String,
            default: undefined,
        },
        enableEmailSegments: {
            type: Boolean,
            default: false,
        },
        disableCompoundSelection: {
            type: Boolean,
            default: false,
        },
        showAsidePanel: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input', 'delete', 'segments-loaded'],
    setup () {
        const { width: windowWidth } = useWindowSize();

        return {
            windowWidth,
        };
    },
    data () {
        return {
            overMauLimitMessage,
            model: null,
            isEditingSegment: false,
            searchValue: '',
            highlightedOptionId: null,
        };
    },
    computed: {
        ...mapState({
            loading: (state) => state.segments.fetching,
            defaultSegments: (state) => state.segments.defaultSegments,
            customSegment: (state) => state.segments.customSegment,
            currentUser: (state) => state.auth.user,
        }),
        ...mapGetters({
            segments: 'segments/list',
            getSegmentGuideEligibility: 'segments/getSegmentGuideEligibility',
            isFreeAndOverMauLimit: 'subscription/isFreeAndOverMauLimit',
            isStarredById: 'stars/isStarredById',
        }),
        shouldReserveSpaceForAside () {
            const viewportOk = shouldShowAsidePanelUtil(this.windowWidth);

            return viewportOk && (this.showAsidePanel || !this.$productLine(PRODUCT_LINES.ADOPT));
        },
        responsiveMinMenuWidth () {
            if (!this.shouldReserveSpaceForAside) return;

            const responsiveWidth = getResponsiveMenuWidth(this.windowWidth);

            return this.minMenuWidth >= 720 ? responsiveWidth : this.minMenuWidth;
        },
        responsiveMaxMenuWidth () {
            if (!this.shouldReserveSpaceForAside) return;

            const responsiveWidth = getResponsiveMenuWidth(this.windowWidth);

            return this.maxMenuWidth >= 720 ? responsiveWidth : this.maxMenuWidth;
        },
        shouldShowAsidePanel () {
            return this.shouldReserveSpaceForAside && !!this.highlightedOptionId;
        },
        segmentChooserPopupType () {
            if (this.allowCustomSegment && !this.shouldReserveSpaceForAside) {
                return 'grid';
            }

            return 'listbox';
        },
        canEdit () {
            return $canSub()
                .entity('segment')
                .action('edit')
                .source('*')
                .target('shared');
        },
        showSelectedOptionInHeader () {
            const everyoneSegmentIds = [
                'everyone',
                'everyoneExcludingAnonymous',
                'anonymousVisitorsOnly',
                'blacklistOnly',
            ];
            if (!this.model) {
                return false;
            }

            if (this.guidesOnly && this.model.id === 'custom') {
                return false;
            }

            if (this.model && everyoneSegmentIds.includes(this.model.id)) {
                return false;
            }

            return true;
        },
        defaultSegmentOptions () {
            const anonDefaultSegmentIds = ['everyoneExcludingAnonymous', 'anonymousVisitorsOnly', 'blacklistOnly'];
            const segments = this.defaultSegments.reduce((acc, segment) => {
                if ((this.guidesOnly || this.excludeAnonDefault) && anonDefaultSegmentIds.includes(segment.id)) {
                    return acc;
                }

                if (!this.allowNoOneSegment && segment.id === 'noOne') {
                    return acc;
                }

                acc.push({
                    ...segment,
                    disabled: this.disabledDefaults && includes(this.disabledDefaults, segment.id),
                });

                return acc;
            }, []);

            if (this.allowCustomSegment) {
                const customSegment = {
                    ...this.customSegment,
                    actions: [
                        {
                            type: 'edit',
                            icon: {
                                type: 'edit',
                            },
                            tooltip: this.$t('segmentHoverCard.customSegment.Edit'),
                            showAlways: true,
                        },
                    ],
                };

                segments.push(customSegment);
            }

            return segments;
        },
        groupedSegmentOptions () {
            const insightsSegments = this.namedSegmentOptions.filter((segment) => segment.isFocusGroup);
            const groups = [];

            groups.push({
                name: 'default',
                id: 'default',
                segments: this.showSelectedOptionInHeader ? [this.model, ...this.defaultSegmentOptions] : this.defaultSegmentOptions,
            });

            if (this.starredSegments.length) {
                groups.push({
                    name: this.$t('segmentChooser.groupedSegmentOptions.Favorites'),
                    id: 'favorites',
                    segments: this.starredSegments,
                });
            } else {
                groups.push({
                    name: this.$t('segmentChooser.groupedSegmentOptions.Favorites'),
                    id: 'favorites',
                    segments: [{
                        id: 'empty-favorites-placeholder',
                        disabled: true,
                    }],
                });
            }

            groups.push({
                name: 'divider',
                segments: this.namedSegmentOptions.filter((segment) => !segment.isFocusGroup),
            });

            if (insightsSegments.length) {
                groups.push({
                    name: this.$t('segmentChooser.groupedSegmentOptions.Insights segments'),
                    id: 'insights',
                    segments: insightsSegments,
                });
            }

            return groups;
        },
        starredSegments () {
            return this.namedSegmentOptions
                .filter((segment) => this.isStarredById(segment.id))
                .map((segment) => ({
                    ...segment,
                    className: 'favorited-segment-option',
                }));
        },
        namedSegmentOptions () {
            return this.segments.map((segment) => {
                let tooltip;
                const actions = [];
                const setTooltip = (content) => {
                    tooltip = { content };
                };

                if (this.shared && !segment.shared) {
                    setTooltip(
                        this.$t(
                            'segmentChooser.namedSegmentOptions.tooltip.Segment cannot be selected: Visibility set to only me',
                        ),
                    );
                }

                if (this.disableFirstVisitFilters && this.isFirstVisitFilter(segment)) {
                    setTooltip(
                        this.$t(
                            'segmentChooser.namedSegmentOptions.tooltip.This segment is unavailable because it contains a Visitor-level "First Visit" rule. The Date Range setting above is used to determine the eligible First Visit range for this widget, which may conflict with this Segment\'s rule.',
                        ),
                    );
                }

                if (this.hasAppIdMismatch(segment)) {
                    setTooltip(
                        this.$t(
                            "segmentChooser.namedSegmentOptions.tooltip.This segment is not defined for this guide's app",
                        ),
                    );
                }

                if (this.disableCompoundSelection && segment.compound) {
                    setTooltip(
                        this.$t(
                            'segmentChooser.namedSegmentOptions.tooltip.This segment cannot be selected: Segment is already referenced by another segment',
                        ),
                    );
                }

                if (this.guidesOnly) {
                    const { eligible, reason } = this.getSegmentGuideEligibility(segment);

                    if (!segment?.shared || (!this.enableEmailSegments && !eligible)) {
                        setTooltip(reason);
                    }
                }

                const canEdit =
                    !segment.isFocusGroup && (this.currentUser.id === segment.createdByUser.id || this.canEdit);
                if (canEdit) {
                    actions.push({
                        type: 'edit',
                        icon: {
                            type: 'edit',
                        },
                        ...(segment.createdByApi
                            ? {
                                disabled: true,
                                tooltip: this.$t(
                                    'segmentChooser.namedSegmentOptions.tooltip.You must use the API to edit API-created segments',
                                ),
                            }
                            : {}),
                    });
                }

                if (!segment.isFocusGroup) {
                    actions.push({
                        type: 'copy',
                        icon: {
                            type: 'copy',
                        },
                        ...(!this.canEdit && segment.compound
                            ? {
                                disabled: true,
                                tooltip: this.$t(
                                    'segmentChooser.namedSegmentOptions.tooltip.You have View Only permission and cannot\ncreate or duplicate nested segments.',
                                ),
                            }
                            : {}),
                        ...(this.isFreeAndOverMauLimit
                            ? {
                                disabled: true,
                                tooltip: overMauLimitMessage,
                            }
                            : {}),
                        ...(segment.createdByApi
                            ? {
                                disabled: true,
                                tooltip: this.$t(
                                    "segmentChooser.namedSegmentOptions.tooltip.You can't duplicate API-created segments",
                                ),
                            }
                            : {}),
                    });
                }

                return {
                    ...segment,
                    disabled: !!tooltip,
                    starred: this.isStarredById(segment.id),
                    tooltip,
                    actions,
                };
            });
        },
        waitingOnValue () {
            return this.waitOnValue ? !this.value : false;
        },
    },
    watch: {
        value (value) {
            this.updateValue(value, false, false);
        },
    },
    async created () {
        await Promise.all([
            this.loadAll({ excludeBlacklist: this.guidesOnly || this.excludeAnonDefault }),
            this.loadAllStars(),
        ]);
        this.$emit('segments-loaded');

        if (this.value && this.value.id) {
            this.updateValue(this.value, false, false);
        }
    },
    methods: {
        ...mapActions('segments', ['createSegment', 'loadAll', 'cloneSegment', 'editSegment', 'editCustomSegment']),
        ...mapActions({
            loadAllStars: 'stars/loadAll',
        }),
        createNewSegment () {
            this.createSegment();
            this.isEditingSegment = true;
        },
        getActions (option) {
            return this.shouldReserveSpaceForAside ? [] : (option?.actions || []);
        },
        isFirstVisitFilter (segment) {
            return get(segment.definition, 'filters').some((filter) => {
                return filter.or
                    ? filter.or.some((or) => or.id === 'visitor_auto_firstvisit' || or.id === 'account_auto_firstvisit')
                    : false || filter.id === 'visitor_auto_firstvisit' || filter.id === 'account_auto_firstvisit';
            });
        },
        updateValue (selected, shouldToggle = false, shouldEmit = true) {
            if (selected?.default) {
                selected.disabled = this.disabledDefaults && includes(this.disabledDefaults, selected.id);
            }

            if (selected?.disabled) {
                return;
            }

            if (shouldToggle) {
                this.$refs.segmentChooser.toggleMenu();
            }

            const allSegments = [...this.defaultSegmentOptions, ...this.namedSegmentOptions];
            const foundSegment = allSegments.find((segment) => segment.id === selected?.id);

            if (foundSegment) {
                this.model = foundSegment;
            } else {
                // This may happen if a segment is private, or deleted,
                // but the caller still has access to a version of it.
                // We'll leave it in the dropdown as a placeholder, but
                // prevent the user from selecting it as a new option
                // once they choose something else.
                this.model = {
                    ...selected,
                    name: this.$t('segmentChooser.updateValue.Unknown Segment'),
                    disabled: true,
                };

                if (!this.allowNoOneSegment && selected?.id === 'noOne') {
                    this.model = {
                        ...selected,
                        name: this.$t('segmentChooser.updateValue.No one'),
                        disabled: true,
                    };
                }
            }

            if (shouldEmit) {
                this.$emit('input', selected);
            }
        },
        isVerified (option) {
            return option.verification?.state === 'Verified';
        },
        edit (option) {
            this.$refs.segmentChooser.toggleMenu();
            this.isEditingSegment = true;
            if (!option || option.id === 'custom') {
                this.editCustomSegment();
            } else {
                this.editSegment({ segmentId: option.id });
            }
        },
        copy (option) {
            this.$refs.segmentChooser.toggleMenu();
            this.cloneSegment({ segmentId: option.id });
            this.isEditingSegment = true;
        },
        deleteSegment (segment) {
            this.isEditingSegment = false;
            if (segment.id === this.model.id) {
                this.model = this.defaultSegmentOptions.find((segment) => segment.id === 'everyone');
                this.updateValue(this.model);
                this.$emit('delete', segment);
            }
        },
        saveSegment (segment) {
            this.model = segment;
            this.updateValue(this.model);
            this.isEditingSegment = false;
            this.$refs.segmentChooser.toggleMenu();
        },
        hasAppIdMismatch (segment) {
            return segmentFiltersHaveAppIdMismatch(segment, this.targetAppId);
        },
        optionName (option) {
            return option?.name;
        },
        optionAside (option) {
            return option?.aside;
        },
        updateSearchValue (value) {
            this.searchValue = value;
        },
        /**
         * @public
         */
        toggleMenu () {
            this.$refs.segmentChooser.toggleMenu();
        },
        onHighlightedOptionChange ($event, clearMultiselectPointer = false) {
            if (!$event) return;

            if ($event.id !== 'empty-favorites-placeholder') {
                this.highlightedOptionId = $event.id;
            }

            if (clearMultiselectPointer) {
                this.$refs.segmentChooser.pointerSet(-1);
            }
        },
        onFilteredOptionsChange (options) {
            const segments = (options || []).flatMap((g) => g.segments || []);
            const visibleIds = segments.map((s) => s.id).filter((id) => id !== 'empty-favorites-placeholder');

            if (visibleIds.length === 0) {
                this.highlightedOptionId = null;
            } else if (this.highlightedOptionId && !visibleIds.includes(this.highlightedOptionId)) {
                this.highlightedOptionId = null;
            }
        },
        onOpen () {
            if (this.showSelectedOptionInHeader && this.model) {
                this.onHighlightedOptionChange(this.model, true);
            }
        },
        onBeforeClose ({ stop }) {
            if (this.isEditingSegment) {
                stop();
            }
        },
        onLeaveLowerBound ({ availableOptions }) {
            onLeaveLowerBound(this.$refs.segmentChooser, availableOptions.length, this.$refs.createNewSegmentButton);
        },
        footerKeydownOverrides () {
            return {
                ...actionButtonKeyOverride(this.createNewSegment),
                ...verticalNavigationKeyOverride(this.$refs.segmentChooser.focusTrigger),
                Home: this.$refs.segmentChooser.focusTrigger,
            };
        },
    },
};
</script>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.segment-chooser__panel--dark {
    .pendo-picker-popper__content {
        border-color: var(--palette-gray-100);
    }
}

</style>

<style scoped lang="less">
:deep(.pendo-multiselect-option__content) {
    flex-grow: 0 !important;
}

.segment-chooser--dark {
    :deep(.pendo-multiselect__trigger) {
        border-color: var(--palette-gray-100);
    }
}

.segment-chooser__option-container {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 4px;

    &:has(.pendo-icon) {
        margin-right: -16px;
    }
}

.segment-chooser__option {
    padding-right: 8px;
    display: grid;
    justify-content: space-between;
    grid-auto-flow: column;
}

.segment-chooser-option__label {
    line-height: 26px;
}

.segment-chooser-option__aside {
    line-height: 26px;
    font-weight: 300;
    color: var(--palette-gray-70);
}

.segment-chooser-option__favorites-placeholder {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 300;
    color: var(--palette-gray-120);
    cursor: default;
}

.segment-chooser__footer {
    border-top: 1px solid var(--palette-gray-30);
    height: 42px;
    line-height: 40px;

    .segment-chooser__create-new-button {
        margin: 4px;
        height: calc(100% - 8px);
        width: calc(100% - 8px);
    }
}
</style>
